import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import ModalComponent from 'components/Common/Modal';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import getAppSite from 'utils/getAppSite';
import CustomFieldItem from './CustomFieldItem';

const DEFAULT_EXPLANATION = 'Emails to this stakeholder will be sent to this address by default.';

const ROW_TOOLTIPS = {
  custom: `A custom email address added by you or a colleague on your team. ${DEFAULT_EXPLANATION}`,
  parliamentary: `This MP's official email address, as published by UK Parliament. ${DEFAULT_EXPLANATION}`,
  ministerial: `This MP's email address for correspondence relating to their ministerial role. ${DEFAULT_EXPLANATION}`,
};

const EmailRowMenu = ({ row, showMakeDefault, saving, onCopy, onEdit, onDelete, onMakeDefault }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const hideTooltip = row.key === 'parliamentary' && getAppSite() === 'usa';

  useOutsideElement(containerRef, () => setOpen(false));

  useEffect(() => {
    if (!open) return;
    const closeOnScroll = () => setOpen(false);
    window.addEventListener('scroll', closeOnScroll, true);
    return () => window.removeEventListener('scroll', closeOnScroll, true);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !menuRef.current || !buttonRef.current) return;
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();
    const updates = {};
    if (menuRect.bottom > window.innerHeight) {
      updates.top = Math.max(4, buttonRect.top - menuRect.height - 4);
    }
    if (menuRect.right > window.innerWidth) {
      updates.left = Math.max(4, buttonRect.right - menuRect.width);
    }
    if (Object.keys(updates).length > 0) {
      setPosition((current) => ({ ...current, ...updates }));
    }
  }, [open]);

  const toggleOpen = () => {
    if (!open && buttonRef.current) {
      const { bottom, left } = buttonRef.current.getBoundingClientRect();
      setPosition({ top: bottom + 4, left });
    }
    setOpen((current) => !current);
  };

  const runAction = (action) => {
    setOpen(false);
    action();
  };

  return (
    <span className='position-relative specific-left-margin-6' ref={containerRef}>
      <button
        className='general-button member-email-button mt-0'
        style={{ transform: 'translateY(2px)' }}
        onClick={toggleOpen}
        ref={buttonRef}
      >
        <span className='icon-three-dots light-40-text paragraph-p3 d-inline-block' />
      </button>
      {open && (
        <div
          ref={menuRef}
          style={{ top: `${position.top}px`, left: `${position.left}px`, width: '250px' }}
          className={`box-shadow rounded ${hideTooltip ? '' : 'pb-2'} position-fixed z-extra-max text-left`}
        >
          {!hideTooltip && (
            <p className='px-3 specific-y-padding-12 mb-2 border-bottom light-60-text paragraph-p2 font-weight-normal'>
              {ROW_TOOLTIPS[row.key]}
            </p>
          )}
          <button
            className='list-action-button w-100 general-button text-left specific-y-padding-10 specific-x-padding-24 flex-centered'
            onClick={() => runAction(onCopy)}
          >
            <span className='icon-copy-thin light-60-text paragraph-p4 font-weight-normal d-inline-block specific-right-margin-12 specific-left-margin-1' />
            Copy email address
          </button>
          {row.key === 'custom' && (
            <>
              <button
                className='list-action-button w-100 general-button text-left specific-y-padding-10 specific-x-padding-24 flex-centered'
                onClick={() => runAction(onEdit)}
              >
                <span className='icon-edit-pencil light-60-text paragraph-p4 font-weight-normal d-inline-block specific-right-margin-10' />
                Edit email address
              </button>
              <button
                className='list-action-button w-100 general-button text-left specific-y-padding-10 specific-x-padding-24 flex-centered'
                onClick={() => runAction(onDelete)}
              >
                <span className='icon-delete light-60-text paragraph-p4 font-weight-normal d-inline-block specific-right-margin-11' />
                Delete email address
              </button>
            </>
          )}
          {showMakeDefault && (
            <button
              className='list-action-button w-100 general-button text-left specific-y-padding-10 specific-x-padding-24 flex-centered'
              onClick={() => runAction(onMakeDefault)}
              disabled={saving}
            >
              {saving ? (
                <i className='fas fa-spinner fa-spin' />
              ) : (
                <span
                  className='icon-circle-check text-light-blue d-inline-block specific-right-margin-10'
                  style={{ marginLeft: '-1px' }}
                />
              )}
              Make default email
            </button>
          )}
        </div>
      )}
    </span>
  );
};

const normalizeEmail = (value) => (value ?? '').trim().toLowerCase();

const StakeholderEmailAddresses = ({ crmEmail, ministerialEmailAddress, positionCategories, onSaveEmail }) => {
  const { email, originalEmailAddress } = crmEmail ?? {};

  const [savingKey, setSavingKey] = useState(null);
  const [pendingDefault, setPendingDefault] = useState(null);
  const [addingCustom, setAddingCustom] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [savingCustom, setSavingCustom] = useState(false);
  const customInputRef = useRef(null);

  useEffect(() => {
    if (addingCustom && customInputRef.current) customInputRef.current.focus();
  }, [addingCustom]);

  const showMinisterial = !!ministerialEmailAddress && positionCategories?.includes('Government');
  const matchesParliamentary = !email || normalizeEmail(email) === normalizeEmail(originalEmailAddress);
  const matchesMinisterial =
    !matchesParliamentary && showMinisterial && normalizeEmail(email) === normalizeEmail(ministerialEmailAddress);
  const isCustomState = !!email && !matchesParliamentary && !matchesMinisterial;

  const rows = [];
  if (isCustomState) {
    rows.push({ key: 'custom', value: email, isDefault: true });
  }
  if (originalEmailAddress) {
    rows.push({ key: 'parliamentary', value: originalEmailAddress, isDefault: matchesParliamentary });
  }
  if (showMinisterial) {
    rows.push({ key: 'ministerial', value: ministerialEmailAddress, isDefault: matchesMinisterial });
  }

  const showDefaultLabels = rows.length > 1;

  const applyDefault = async (row) => {
    setSavingKey(row.key);
    try {
      await onSaveEmail(row.key === 'parliamentary' || row.key === 'delete' ? null : row.value);
      setPendingDefault(null);
    } catch {
    } finally {
      setSavingKey(null);
    }
  };

  const copyToClipboard = (value) => {
    navigator.clipboard
      .writeText(value)
      .then(() => createNotification('success', 'Email address has been copied'))
      .catch(() => createNotification('danger', 'Could not copy email address'));
  };

  const handleMakeDefault = (row) => {
    if (isCustomState) {
      setPendingDefault(row);
    } else {
      applyDefault(row);
    }
  };

  const startEditCustom = () => {
    setCustomValue(isCustomState ? email : '');
    setAddingCustom(true);
  };

  const handleDeleteCustom = () => {
    setPendingDefault({ key: 'delete', value: null });
  };

  const saveCustomEmail = async () => {
    if (!customValue || customValue === email) {
      setAddingCustom(false);
      return;
    }
    setSavingCustom(true);
    try {
      await onSaveEmail(customValue);
      setAddingCustom(false);
    } catch {
    } finally {
      setSavingCustom(false);
    }
  };

  const customEmailInput = (
    <>
      <input
        ref={customInputRef}
        type='text'
        value={customValue}
        disabled={savingCustom}
        onChange={(e) => setCustomValue(e.target.value)}
        onKeyDown={(e) => {
          if (savingCustom) return;
          if (e.key === 'Enter') saveCustomEmail();
          if (e.key === 'Escape') setAddingCustom(false);
        }}
        placeholder='Add a custom email'
        autoComplete='off'
        className='crm-table-editable-text-input flex-grow-1'
      />
      {savingCustom ? (
        <span className='ml-1 bg-light-blue rounded-circle d-flex align-items-center justify-content-center circle-button-24'>
          <i className='fas fa-spinner fa-spin text-white paragraph-p3' />
        </span>
      ) : (
        <span
          className='icon-circle-check ml-1 text-light-blue title-h2 pointer hover-opacity-85'
          onMouseDown={(e) => e.preventDefault()}
          onClick={saveCustomEmail}
        />
      )}
    </>
  );

  if (rows.length === 0) {
    return <CustomFieldItem label='Email' name='email' initialValue='' onSave={onSaveEmail} />;
  }

  return (
    <>
      {rows.map((row) => (
        <React.Fragment key={row.key}>
          <h4 className='category-subtitle member-info-subtitle d-flex align-items-center'>
            <span>{row.key === 'parliamentary' && getAppSite() === 'usa' ? 'Email' : `Email (${row.key})`}</span>
            {row.isDefault && (
              <span className='light-60-text paragraph-p3 font-weight-normal ml-2'>Default</span>
            )}
            <EmailRowMenu
              row={row}
              showMakeDefault={
                showDefaultLabels && !row.isDefault && !(row.key === 'parliamentary' && getAppSite() === 'usa')
              }
              saving={savingKey === row.key}
              onCopy={() => copyToClipboard(row.value)}
              onEdit={startEditCustom}
              onDelete={handleDeleteCustom}
              onMakeDefault={() => handleMakeDefault(row)}
            />
          </h4>
          <p className='mb-0 member-data mt-2 d-flex align-items-center'>
            {row.key === 'custom' && addingCustom ? (
              <span className='d-flex align-items-center flex-grow-1'>{customEmailInput}</span>
            ) : (
              <span className='line-height-1-5-rem'>{row.value}</span>
            )}
          </p>
        </React.Fragment>
      ))}

      {!isCustomState &&
        (addingCustom ? (
          <p className='mb-0 member-data specific-top-margin-16 d-flex align-items-center'>{customEmailInput}</p>
        ) : (
          <p className='mb-0 member-data specific-top-margin-16'>
            <button className='general-button simple-link-button' onClick={startEditCustom}>
              + Add a custom email address
            </button>
          </p>
        ))}

      {pendingDefault && (
        <ModalComponent isOpen={!!pendingDefault} setIsOpen={() => setPendingDefault(null)} maxWidth={480}>
          <div className='p-4 pt-5 text-center'>
            <p className='mt-3 mb-4 paragraph-p1'>Warning - this will delete your custom email address</p>
            <div className='d-flex justify-content-center'>
              <button
                className='danger-button-outlined px-3 py-2 mr-3'
                onClick={() => setPendingDefault(null)}
                disabled={!!savingKey}
              >
                Cancel
              </button>
              <button
                className='danger-button px-3 py-2'
                onClick={() => applyDefault(pendingDefault)}
                disabled={savingKey === pendingDefault.key}
              >
                {savingKey === pendingDefault.key ? (
                  <i className='fas fa-spinner fa-spin' />
                ) : pendingDefault.key === 'delete' ? (
                  'Delete'
                ) : (
                  'Make default email'
                )}
              </button>
            </div>
          </div>
        </ModalComponent>
      )}
    </>
  );
};

export default StakeholderEmailAddresses;
